import SearchIcon from "../../assets/images/Search.svg";
import { Fragment, useCallback, useState } from "react";
import { IReport } from "../../helpers/Interfaces";
import { convertDuration, splitWord, validDateField, validStringField } from "../../helpers/HelperFunctions";
import { ReportFilterTypes } from "../../helpers/Types";
import SearchableDown from "../../components/SearchableDown/SearchableDown";
import RemoveFilter from "../../assets/images/RemoveFilter.svg";
import RightArrow from "../../assets/images/ArrowRight.svg";
import DropdownIcon from "../../assets/images/Dropdown.svg";
import { Checkbox } from "@mui/material";
import BlueHomeDot from "../../assets/images/BlueHomeDot.svg";
import BlueHomeDotDisable from "../../assets/images/BlueHomeDotDisable.svg";
import { Colors } from "../../helpers/Colors";
import Trash from "../../assets/images/Trash2.svg";
import Download from "../../assets/images/Download.svg";

import "./Reports.scss";

type DraftReportsUiProps = {
  reports: IReport[],
  setSelectedReport: CallableFunction;
  openView: CallableFunction;
  filter: (filterBy: ReportFilterTypes, query: string) => void;
  cancelFilter: boolean;
  resetDefault: CallableFunction
  tickReport: (reportId: string) => void;
  toggleAllReports: () => void;
  isReportTicked: (reportId: string) => boolean;
  showDelete: boolean;
  showDownload: boolean;
  areAllReportsTicked: () => boolean;
}

export default function DraftReportsUI(props: DraftReportsUiProps) {

  const [anchorSearcherEl, setAnchorSearcherEl] = useState<HTMLImageElement>(null as any);
  const [selectedFilter, setSelectedFilter] = useState<ReportFilterTypes>("patientName");
  const [isAscending, setIsAscending] = useState(true);

  const openSearch = Boolean(anchorSearcherEl);

  const handleClickView = useCallback((selectedReport: IReport) => {
    props.setSelectedReport(selectedReport);
    props.openView("view");
  }, []);

  const handleSearchOpen = (event: React.MouseEvent<HTMLImageElement>, filterBy: ReportFilterTypes) => {
    setAnchorSearcherEl(event.currentTarget);
    setSelectedFilter(filterBy);
  };

  const handleSearchClose = () => {
    setAnchorSearcherEl(null as any);
  };

  const handleSelectedItem = (selectedValue: string) => {
    props.filter(selectedFilter, selectedValue);
    setAnchorSearcherEl(null as any);
  };

  const handleClearFilter = () => {
    props.resetDefault()
  };

  function getInitialValues() {

    let valueArr = [] as any[];

    props.reports.forEach(report => {
      valueArr.push(report[selectedFilter])
    });

    let distinctValues = valueArr.filter((value, index, array) => array.indexOf(value) === index);

    return distinctValues;
  }

  function getSortedReports() {
    const allReports = [...props.reports];

    return allReports.sort((a, b) => {
      const timeA = a.dateOfAssessment ? new Date(a.dateOfAssessment).getTime() : Infinity;
      const timeB = b.dateOfAssessment ? new Date(b.dateOfAssessment).getTime() : Infinity;

      return isAscending ? timeA - timeB : timeB - timeA;
    });
  }

  return (
    <div className="table animated animatedFadeInUp fadeInUp">
      <div className="header">
        <div className="header-row small">
          <Checkbox icon={<img alt='check box' src={BlueHomeDotDisable} />}
            checkedIcon={<img alt='check' src={BlueHomeDot} />}
            value="cryon" style={{ color: Colors.white_10 }}
            checked={props.areAllReportsTicked()}
            onClick={(e) => { props.toggleAllReports(); e.stopPropagation(); }}
          />
        </div>
        <div className="header-row large">
          <span className="large-text">Patient Name</span>
          <img onClick={(value) => handleSearchOpen(value, "patientName")} className="icon" src={SearchIcon} alt={"Search"} />
        </div>
        <div className="header-row small">
          <span className="large-text">Report Type</span>
          <img onClick={(value) => handleSearchOpen(value, "reportType")} className="icon" src={SearchIcon} alt={"Search"} />
        </div>
        <div className="header-row large">
          <span className="large-text">Date of Assessment</span>
          <img onClick={() => setIsAscending(!isAscending)} src={DropdownIcon} alt="SortArrow" className={isAscending ? "img" : "invert-img"} />
        </div>
        <div className="header-row small">
          <span className="large-text">Due Date</span>
        </div>
        <div className="header-row large">
          <span className="large-text">Clinic</span>
          <img onClick={(value) => handleSearchOpen(value, "clinic")} className="icon" src={SearchIcon} alt={"Search"} />
        </div>
        <div className="header-row small">
          <span className="large-text">Audio Duration</span>
        </div>
        <div className="header-row small">
          <span className="large-text">Paperwork Received</span>
        </div>
        <div className="header-row small">
          <span className="large-text">Invoice</span>
        </div>
        <div className="header-row small">
          <div className="filter-content">
            {props.cancelFilter
              ? <img className="filter-icon" src={RemoveFilter} alt={"RemoveFilter"} onClick={handleClearFilter} />
              : <Fragment />
            }
            {props.showDownload
              ? <img className="filter-icon" src={Download} alt={"Download"} onClick={() => { props.openView("bulkDownload"); }} />
              : <Fragment />
            }
            {props.showDelete
              ? <img className="filter-icon" src={Trash} alt={"Trash"} onClick={() => { props.openView("bulkDelete"); }} />
              : <Fragment />
            }
          </div>
        </div>
      </div>
      <div className="data animated animatedFadeInUp fadeInUp">
        {getSortedReports().map((report) => {
          return (<div key={report.id} className="data-row" onClick={() => { handleClickView(report); }} >
            <span className="cell large-text small">
              <Checkbox icon={<img alt='check box' src={BlueHomeDotDisable} />}
                checkedIcon={<img alt='check' src={BlueHomeDot} />}
                value="cryon" style={{ color: Colors.white_10 }}
                checked={props.isReportTicked(report.id)}
                onClick={(e) => {
                  props.tickReport(report.id); e.stopPropagation();
                }} />
            </span>
            <span className="cell large-text large">{validStringField(report.patientName)}</span>
            <span className="cell large-text small">{validStringField(report.reportType)}</span>
            <span className="cell large-text large">
              <div className="cell text small">{validDateField(report.dateOfAssessment)}</div>
            </span>
            <span className="cell large-text small">
              <div className="cell text small">{validDateField(report.dueDate)}</div>
            </span>
            <span className="cell large-text large">{validStringField(report.clinic)}</span>
            <span className="cell large-text small">
              {convertDuration(report.totalDuration)}
            </span>
            <span className="cell large-text small">{report.paperworkReceived ? "Paperwork Received" : "No"}</span>
            <span className="cell large-text small">{splitWord(report.invoiceStatus)}</span>
            <span className="cell large-text small flex-end">
              <div>
                <img className="action" src={RightArrow} alt={"RightArrow"} />
              </div>
            </span>
          </div>);
        })}
      </div>

      {openSearch
        ? (<SearchableDown open={openSearch} anchorEl={anchorSearcherEl} onClose={handleSearchClose}
          initialDetails={getInitialValues()} onClick={(selectedValue: string) => { handleSelectedItem(selectedValue); }} searchable />)
        : (<Fragment />)
      }
    </div>
  );
}

